import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Avatar,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  alpha,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  Badge,
  Fade,
  Zoom,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Import icons
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleIcon from '@mui/icons-material/People';
import HotelIcon from '@mui/icons-material/Hotel';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import PoolIcon from '@mui/icons-material/Pool';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PendingIcon from '@mui/icons-material/Pending';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaymentIcon from '@mui/icons-material/Payment';
import CategoryIcon from '@mui/icons-material/Category';
import DateRangeIcon from '@mui/icons-material/DateRange';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import SavingsIcon from '@mui/icons-material/Savings';

// Import Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  Filler,
} from 'chart.js';
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';

// Import local components
import Sidebar from '../../../../components/sidebars/Sidebar_hotelAccount_cashier';
import TopNav from '../../../../components/TopNav_hotelStaff';
import axios from 'axios';
import * as XLSX from 'xlsx';

// Register ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
  Filler
);

// Color palette
const DEPARTMENT_COLORS = {
  'Room Bookings': '#6366F1',
  'Food & Beverage': '#10B981',
  'Facilities': '#F59E0B',
  'Other Incomes': '#8B5CF6',
  'Expenses': '#EF4444',
};

const PAYMENT_METHOD_COLORS = {
  'Cash': '#10B981',
  'Bank Transfer': '#3B82F6',
  'POS Terminal': '#F59E0B',
  'QR Code': '#8B5CF6',
  'Complimentary': '#EC4899',
};

const STATUS_COLORS = {
  'completed': '#10B981',
  'paid': '#10B981',
  'pending': '#F59E0B',
  'refunded': '#EF4444',
  'cancelled': '#6B7280',
};

const AccountDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [transactionsDialogOpen, setTransactionsDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [expensesDialogOpen, setExpensesDialogOpen] = useState(false);
  const [selectedDepartmentData, setSelectedDepartmentData] = useState(null);
  const [expensesData, setExpensesData] = useState([]);
  
  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  
  const userProfile = JSON.parse(localStorage.getItem("userProfile"));
  const hotel_code = userProfile?.hotel_code || "";
  const hotelName = userProfile?.hotel_name || "Hotel";

  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userProfile');
    navigate('/login');
  };

  const handleNotificationClick = () => {
    // Handle notifications
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!hotel_code) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const baseURL = process.env.REACT_APP_API_BASE_URL || '';
        const formattedDate = selectedDate.toISOString().split('T')[0];
        
        const response = await axios.get(`${baseURL}/hotel/account/statistics.php`, {
          params: { hotel_code, date: formattedDate }
        });
        
        if (response.data.success) {
          setDashboardData(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [hotel_code, selectedDate]);

  // Fetch expenses data separately
  useEffect(() => {
    const fetchExpenses = async () => {
      if (!hotel_code) return;
      
      try {
        const baseURL = process.env.REACT_APP_API_BASE_URL || '';
        const formattedDate = selectedDate.toISOString().split('T')[0];
        
        const response = await axios.get(`${baseURL}/hotel/account/get_expenses.php`, {
          params: { hotel_code, date: formattedDate }
        });
        
        if (response.data.success) {
          setExpensesData(response.data.data || []);
        }
      } catch (error) {
        console.error('Error fetching expenses:', error);
      }
    };
    
    fetchExpenses();
  }, [hotel_code, selectedDate]);

  // Filter and sort transactions
  const filteredTransactions = useMemo(() => {
    if (!dashboardData?.transactions) return [];
    
    let filtered = [...dashboardData.transactions];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.processed_by?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply department filter
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(t => t.department === departmentFilter);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      
      if (orderBy === 'amount') {
        aVal = parseFloat(aVal);
        bVal = parseFloat(bVal);
      }
      
      if (orderBy === 'date') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }
      
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  }, [dashboardData, searchTerm, departmentFilter, orderBy, order]);

  // Paginated transactions
  const paginatedTransactions = filteredTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleCardClick = (department, data) => {
    // Don't show popup for All Departments
    if (department === 'All Departments') {
      return;
    }
    setSelectedDepartmentData({ department, ...data });
    setTransactionsDialogOpen(true);
  };

  const handlePaymentAnalysisClick = (department, data) => {
    setSelectedDepartmentData({ department, ...data });
    setPaymentDialogOpen(true);
  };

  const handleExpensesClick = () => {
    setExpensesDialogOpen(true);
  };

  // Export to Excel
  const exportToExcel = () => {
    const exportData = filteredTransactions.map(t => ({
      'Date': new Date(t.date).toLocaleString(),
      'Department': t.department,
      'Customer': t.customer_name,
      'Amount': t.amount,
      'Payment Method': t.payment_method,
      'Status': t.status,
      'Processed By': t.processed_by,
      'Reference': t.reference
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
    XLSX.writeFile(wb, `transactions_${selectedDate.toISOString().split('T')[0]}.xlsx`);
    handleMenuClose();
  };

  // Export to PDF (print)
  const exportToPDF = () => {
    window.print();
    handleMenuClose();
  };

  const stats = dashboardData?.today_stats || {};
  const revenueBreakdown = dashboardData?.revenue_breakdown || [];
  const transactions = dashboardData?.transactions || [];
  const paymentAnalysis = dashboardData?.payment_analysis || [];
  const weeklyTrend = dashboardData?.weekly_trend || [];

  // Calculate total expenses
  const totalExpenses = expensesData.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
  const netIncome = (stats.total_income_today || 0) - totalExpenses;

  // Calculate percentages for revenue breakdown
  const totalRevenue = stats.total_income_today || 0;
  const revenueWithPercentage = revenueBreakdown.map(item => ({
    ...item,
    percentage: totalRevenue > 0 ? (item.revenue / totalRevenue) * 100 : 0
  }));

  // Chart data for weekly trend
  const weeklyTrendData = {
    labels: weeklyTrend.map(item => item.day),
    datasets: [
      {
        label: 'Room Bookings',
        data: weeklyTrend.map(item => item.room_revenue),
        backgroundColor: DEPARTMENT_COLORS['Room Bookings'],
        borderRadius: 4,
      },
      {
        label: 'F&B',
        data: weeklyTrend.map(item => item.fb_revenue),
        backgroundColor: DEPARTMENT_COLORS['Food & Beverage'],
        borderRadius: 4,
      },
      {
        label: 'Facilities',
        data: weeklyTrend.map(item => item.facility_revenue),
        backgroundColor: DEPARTMENT_COLORS['Facilities'],
        borderRadius: 4,
      },
      {
        label: 'Other',
        data: weeklyTrend.map(item => item.other_revenue),
        backgroundColor: DEPARTMENT_COLORS['Other Incomes'],
        borderRadius: 4,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { font: { size: 11 } } },
      tooltip: { callbacks: { label: (ctx) => `₦${ctx.raw.toLocaleString()}` } },
    },
    scales: {
      x: { stacked: true },
      y: { stacked: true, ticks: { callback: (v) => `₦${v.toLocaleString()}` } },
    },
  };

  const TransactionDialog = () => {
    // Get transactions filtered by the selected department
    const departmentTransactions = transactions.filter(
      t => t.department === selectedDepartmentData?.department
    );

    // Calculate total for the department
    const departmentTotal = departmentTransactions.reduce(
      (sum, t) => sum + parseFloat(t.amount || 0), 
      0
    );

    // Don't show popup for "All Departments"
    if (selectedDepartmentData?.department === 'All Departments') {
      setTransactionsDialogOpen(false);
      return null;
    }

    return (
      <Dialog 
        open={transactionsDialogOpen} 
        onClose={() => setTransactionsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {selectedDepartmentData?.department || 'Department'} Transactions
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total: ₦{departmentTotal.toLocaleString()} | Count: {departmentTransactions.length}
            </Typography>
          </Box>
          <IconButton onClick={() => setTransactionsDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {departmentTransactions.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No transactions found for {selectedDepartmentData?.department} on this date.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8fafc' }}>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Customer</TableCell>
                    
                    {/* Room Bookings specific columns */}
                    {selectedDepartmentData?.department === 'Room Bookings' && (
                      <>
                        <TableCell>Room Number</TableCell>
                        <TableCell>Staff Name</TableCell>
                      </>
                    )}
                    
                    {/* Food & Beverage specific columns */}
                    {selectedDepartmentData?.department === 'Food & Beverage' && (
                      <>
                        <TableCell>Order Type</TableCell>
                        <TableCell>Order Number</TableCell>
                        <TableCell>Table Number</TableCell>
                        <TableCell>Staff Name</TableCell>
                      </>
                    )}
                    
                    {/* Facilities specific columns */}
                    {selectedDepartmentData?.department === 'Facilities' && (
                      <>
                        <TableCell>Staff Name</TableCell>
                      </>
                    )}
                    
                    {/* Other Incomes specific columns */}
                    {selectedDepartmentData?.department === 'Other Incomes' && (
                      <>
                        <TableCell>Category</TableCell>
                        <TableCell>Staff Name</TableCell>
                      </>
                    )}
                    
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Reference</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {departmentTransactions.map((t, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell>{new Date(t.date).toLocaleString()}</TableCell>
                      <TableCell>{t.customer_name || 'N/A'}</TableCell>
                      
                      {/* Room Bookings specific data */}
                      {selectedDepartmentData?.department === 'Room Bookings' && (
                        <>
                          <TableCell>
                            <Chip 
                              label={t.room_number || 'N/A'} 
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{t.processed_by || 'N/A'}</TableCell>
                        </>
                      )}
                      
                      {/* Food & Beverage specific data */}
                      {selectedDepartmentData?.department === 'Food & Beverage' && (
                        <>
                          <TableCell>
                            <Chip 
                              label={t.order_type || 'N/A'} 
                              size="small"
                              sx={{ 
                                bgcolor: t.order_type === 'dine_in' ? alpha('#10B981', 0.1) : 
                                        t.order_type === 'room_service' ? alpha('#3B82F6', 0.1) :
                                        t.order_type === 'takeaway' ? alpha('#F59E0B', 0.1) :
                                        t.order_type === 'delivery' ? alpha('#8B5CF6', 0.1) :
                                        alpha('#6B7280', 0.1),
                                color: t.order_type === 'dine_in' ? '#10B981' : 
                                       t.order_type === 'room_service' ? '#3B82F6' :
                                       t.order_type === 'takeaway' ? '#F59E0B' :
                                       t.order_type === 'delivery' ? '#8B5CF6' : '#6B7280'
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {t.reference || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>{t.table_number || 'N/A'}</TableCell>
                          <TableCell>{t.processed_by || 'N/A'}</TableCell>
                        </>
                      )}
                      
                      {/* Facilities specific data */}
                      {selectedDepartmentData?.department === 'Facilities' && (
                        <>
                          <TableCell>{t.processed_by || 'System'}</TableCell>
                        </>
                      )}
                      
                      {/* Other Incomes specific data */}
                      {selectedDepartmentData?.department === 'Other Incomes' && (
                        <>
                          <TableCell>
                            <Chip 
                              label={t.category || 'N/A'} 
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{t.processed_by || 'N/A'}</TableCell>
                        </>
                      )}
                      
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        ₦{parseFloat(t.amount || 0).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={t.status || 'N/A'} 
                          size="small"
                          sx={{ 
                            bgcolor: alpha(STATUS_COLORS[t.status] || '#6B7280', 0.1), 
                            color: STATUS_COLORS[t.status] || '#6B7280' 
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {t.reference || 'N/A'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransactionsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const PaymentDialog = () => {
    const deptData = paymentAnalysis.find(
      d => d.department === selectedDepartmentData?.department
    );

    return (
      <Dialog 
        open={paymentDialogOpen} 
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              {selectedDepartmentData?.department} - Payment Methods
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total: ₦{deptData?.total?.toLocaleString() || 0}
            </Typography>
          </Box>
          <IconButton onClick={() => setPaymentDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!deptData?.methods || deptData.methods.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No payment data available for {selectedDepartmentData?.department}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {deptData.methods.map((method, idx) => {
                const percentage = deptData.total > 0 
                  ? (method.total_amount / deptData.total) * 100 
                  : 0;
                
                return (
                  <Grid item xs={12} key={idx}>
                    <Paper sx={{ p: 2, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {method.payment_method || 'N/A'}
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          ₦{method.total_amount?.toLocaleString() || 0}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                        <Chip label={`${method.cnt || method.count || 0} transactions`} size="small" />
                        <Chip 
                          label={`${percentage.toFixed(1)}%`} 
                          size="small" 
                          color="primary" 
                        />
                      </Box>
                      <Box sx={{ bgcolor: '#e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                        <Box 
                          sx={{ 
                            width: `${percentage}%`, 
                            bgcolor: PAYMENT_METHOD_COLORS[method.payment_method] || deptData.color, 
                            height: 6 
                          }} 
                        />
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const ExpensesDialog = () => {
    // Group expenses by category
    const expensesByCategory = expensesData.reduce((acc, exp) => {
      const category = exp.category || 'Other';
      if (!acc[category]) {
        acc[category] = { total: 0, items: [] };
      }
      acc[category].total += parseFloat(exp.amount || 0);
      acc[category].items.push(exp);
      return acc;
    }, {});

    const totalExpensesAmount = expensesData.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);

    return (
      <Dialog 
        open={expensesDialogOpen} 
        onClose={() => setExpensesDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">
              Expenses for {selectedDate.toLocaleDateString()}
            </Typography>
            <Typography variant="body2" color="error.main">
              Total Expenses: ₦{totalExpensesAmount.toLocaleString()}
            </Typography>
          </Box>
          <IconButton onClick={() => setExpensesDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {expensesData.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                No expenses recorded for this date.
              </Typography>
            </Box>
          ) : (
            <>
              {/* Category Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {Object.entries(expensesByCategory).map(([category, data]) => (
                  <Grid item xs={12} sm={6} md={3} key={category}>
                    <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#EF4444', 0.05) }}>
                      <Typography variant="caption" color="text.secondary">{category}</Typography>
                      <Typography variant="h6" fontWeight={700} color="error.main">
                        ₦{data.total.toLocaleString()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {data.items.length} transaction(s)
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Expenses Table */}
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell>Date & Time</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Paid To</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Payment Method</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Staff</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expensesData.map((expense, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{new Date(expense.created_at).toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip 
                            label={expense.category} 
                            size="small"
                            sx={{ bgcolor: alpha('#EF4444', 0.1), color: '#EF4444' }}
                          />
                        </TableCell>
                        <TableCell>{expense.paid_to}</TableCell>
                        <TableCell>{expense.description}</TableCell>
                        <TableCell>
                          <Chip 
                            label={expense.payment_method} 
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: 'error.main' }}>
                          ₦{parseFloat(expense.amount).toLocaleString()}
                        </TableCell>
                        <TableCell>{expense.staff_name || expense.staff_id || 'System'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpensesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  if (!hotel_code) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Typography>Please log in again</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f0f2f5' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        
        <Box 
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 3 },
            ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
            mt: 0,
            maxWidth: '1400px',
            mx: 'auto',
          }}
        >
          <TopNav onLogout={handleLogout} onNotificationClick={handleNotificationClick} toggleSidebar={toggleSidebar} />
          
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight={700}>Financial Dashboard</Typography>
              <Typography variant="body2" color="text.secondary">{hotelName} • Daily Financial Report</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={(newDate) => setSelectedDate(newDate || new Date())}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 150, bgcolor: 'white', borderRadius: 2 } } }}
              />
              <Tooltip title="Refresh">
                <IconButton onClick={() => window.location.reload()} sx={{ bgcolor: 'white' }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export">
                <IconButton onClick={handleMenuOpen} sx={{ bgcolor: 'white' }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={exportToExcel}><TableChartIcon sx={{ mr: 1, fontSize: 18 }} /> Export to Excel</MenuItem>
                <MenuItem onClick={exportToPDF}><PictureAsPdfIcon sx={{ mr: 1, fontSize: 18 }} /> Export to PDF</MenuItem>
                <MenuItem onClick={handleMenuClose}><PrintIcon sx={{ mr: 1, fontSize: 18 }} /> Print</MenuItem>
              </Menu>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : (
            <>
              {/* Income Summary Cards */}
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Financial Summary for {selectedDate.toLocaleDateString()} (Click on cards to view details)
              </Typography>
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Total Income Card */}
                <Grid item xs={12} sm={6} md={2.4}>
                  <Zoom in={true} style={{ transitionDelay: '0ms' }}>
                    <Paper 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-4px)' }
                      }}
                      onClick={() => handleCardClick('All Departments', { revenue: stats.total_income_today, count: transactions.length })}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Income</Typography>
                          <Typography variant="h4" fontWeight={700}>₦{stats.total_income_today?.toLocaleString()}</Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>{transactions.length} transactions</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha('#fff', 0.2), color: 'white' }}>
                          <AccountBalanceWalletIcon />
                        </Avatar>
                      </Box>
                    </Paper>
                  </Zoom>
                </Grid>

                {/* Room Bookings Card */}
                <Grid item xs={12} sm={6} md={2.4}>
                  <Zoom in={true} style={{ transitionDelay: '100ms' }}>
                    <Paper 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3,
                        borderTop: `4px solid ${DEPARTMENT_COLORS['Room Bookings']}`,
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 }
                      }}
                      onClick={() => handleCardClick('Room Bookings', stats.room_bookings)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Room Bookings</Typography>
                          <Typography variant="h4" fontWeight={700}>₦{stats.room_bookings?.revenue?.toLocaleString()}</Typography>
                          <Typography variant="caption" color="text.secondary">{stats.room_bookings?.count || 0} bookings</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(DEPARTMENT_COLORS['Room Bookings'], 0.1), color: DEPARTMENT_COLORS['Room Bookings'] }}>
                          <HotelIcon />
                        </Avatar>
                      </Box>
                    </Paper>
                  </Zoom>
                </Grid>

                {/* F&B Card */}
                <Grid item xs={12} sm={6} md={2.4}>
                  <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                    <Paper 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3,
                        borderTop: `4px solid ${DEPARTMENT_COLORS['Food & Beverage']}`,
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 }
                      }}
                      onClick={() => handleCardClick('Food & Beverage', stats.fb_income)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Food & Beverage</Typography>
                          <Typography variant="h4" fontWeight={700}>₦{stats.fb_income?.revenue?.toLocaleString()}</Typography>
                          <Typography variant="caption" color="text.secondary">{stats.fb_income?.count || 0} orders</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(DEPARTMENT_COLORS['Food & Beverage'], 0.1), color: DEPARTMENT_COLORS['Food & Beverage'] }}>
                          <RestaurantIcon />
                        </Avatar>
                      </Box>
                    </Paper>
                  </Zoom>
                </Grid>

                {/* Facilities Card */}
                <Grid item xs={12} sm={6} md={2.4}>
                  <Zoom in={true} style={{ transitionDelay: '300ms' }}>
                    <Paper 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3,
                        borderTop: `4px solid ${DEPARTMENT_COLORS['Facilities']}`,
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 }
                      }}
                      onClick={() => handleCardClick('Facilities', stats.facility_income)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Facilities</Typography>
                          <Typography variant="h4" fontWeight={700}>₦{stats.facility_income?.revenue?.toLocaleString()}</Typography>
                          <Typography variant="caption" color="text.secondary">{stats.facility_income?.count || 0} bookings</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(DEPARTMENT_COLORS['Facilities'], 0.1), color: DEPARTMENT_COLORS['Facilities'] }}>
                          <PoolIcon />
                        </Avatar>
                      </Box>
                    </Paper>
                  </Zoom>
                </Grid>

                {/* Other Incomes Card */}
                <Grid item xs={12} sm={6} md={2.4}>
                  <Zoom in={true} style={{ transitionDelay: '400ms' }}>
                    <Paper 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3,
                        borderTop: `4px solid ${DEPARTMENT_COLORS['Other Incomes']}`,
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 }
                      }}
                      onClick={() => handleCardClick('Other Incomes', stats.other_incomes)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Other Incomes</Typography>
                          <Typography variant="h4" fontWeight={700}>₦{stats.other_incomes?.revenue?.toLocaleString()}</Typography>
                          <Typography variant="caption" color="text.secondary">{stats.other_incomes?.count || 0} entries</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(DEPARTMENT_COLORS['Other Incomes'], 0.1), color: DEPARTMENT_COLORS['Other Incomes'] }}>
                          <PaymentIcon />
                        </Avatar>
                      </Box>
                    </Paper>
                  </Zoom>
                </Grid>
              </Grid>

              {/* Second Row: Expenses and Net Income */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Expenses Card */}
                <Grid item xs={12} sm={6} md={3}>
                  <Zoom in={true} style={{ transitionDelay: '500ms' }}>
                    <Paper 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3,
                        borderTop: `4px solid ${DEPARTMENT_COLORS['Expenses']}`,
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 }
                      }}
                      onClick={handleExpensesClick}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">Expenses</Typography>
                          <Typography variant="h4" fontWeight={700} color="error.main">
                            ₦{totalExpenses.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{expensesData.length} transactions</Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha(DEPARTMENT_COLORS['Expenses'], 0.1), color: DEPARTMENT_COLORS['Expenses'] }}>
                          <MoneyOffIcon />
                        </Avatar>
                      </Box>
                    </Paper>
                  </Zoom>
                </Grid>

                {/* Net Income Card */}
                <Grid item xs={12} sm={6} md={3}>
                  <Zoom in={true} style={{ transitionDelay: '600ms' }}>
                    <Paper 
                      sx={{ 
                        p: 2.5, 
                        borderRadius: 3,
                        background: netIncome >= 0 
                          ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                          : 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                        color: 'white',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 3 }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" sx={{ opacity: 0.9 }}>Net Income</Typography>
                          <Typography variant="h4" fontWeight={700}>
                            ₦{netIncome.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {netIncome >= 0 ? 'Profit' : 'Loss'}
                          </Typography>
                        </Box>
                        <Avatar sx={{ bgcolor: alpha('#fff', 0.2), color: 'white' }}>
                          <SavingsIcon />
                        </Avatar>
                      </Box>
                    </Paper>
                  </Zoom>
                </Grid>
              </Grid>

              {/* Tabs Section */}
              <Paper sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ px: 2, pt: 1, borderBottom: '1px solid #e2e8f0' }}>
                  <Tab label="Revenue Overview" />
                  <Tab label="Transactions" />
                  <Tab label="Payment Analysis" />
                  <Tab label="Weekly Trend" />
                </Tabs>

                {/* Revenue Overview Tab */}
                {activeTab === 0 && (
                  <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Revenue Breakdown by Department</Typography>
                        <Box sx={{ height: 300 }}>
                          <Doughnut 
                            data={{
                              labels: revenueWithPercentage.map(r => r.department),
                              datasets: [{
                                data: revenueWithPercentage.map(r => r.revenue),
                                backgroundColor: revenueWithPercentage.map(r => r.color),
                                borderColor: '#fff',
                                borderWidth: 2,
                              }],
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: { position: 'bottom' },
                                tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ₦${ctx.raw.toLocaleString()} (${ctx.parsed}%)` } }
                              }
                            }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>Department Breakdown</Typography>
                        <Stack spacing={2}>
                          {revenueWithPercentage.map((item, idx) => (
                            <Paper key={idx} sx={{ p: 2, borderRadius: 2 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography variant="subtitle2" fontWeight={600}>{item.department}</Typography>
                                <Typography variant="h6" fontWeight={700}>₦{item.revenue?.toLocaleString()}</Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box sx={{ flexGrow: 1, bgcolor: '#e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                                  <Box sx={{ width: `${item.percentage}%`, bgcolor: item.color, height: 8 }} />
                                </Box>
                                <Typography variant="caption" color="text.secondary">{item.percentage.toFixed(1)}%</Typography>
                              </Box>
                            </Paper>
                          ))}
                          {/* Expenses Row in Breakdown */}
                          <Paper sx={{ p: 2, borderRadius: 2, bgcolor: alpha('#EF4444', 0.05) }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle2" fontWeight={600} color="error.main">Expenses</Typography>
                              <Typography variant="h6" fontWeight={700} color="error.main">- ₦{totalExpenses.toLocaleString()}</Typography>
                            </Box>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="subtitle2" fontWeight={700}>Net Income</Typography>
                              <Typography variant="h6" fontWeight={700} color={netIncome >= 0 ? 'success.main' : 'error.main'}>
                                ₦{netIncome.toLocaleString()}
                              </Typography>
                            </Box>
                          </Paper>
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                {/* Transactions Tab */}
                {activeTab === 1 && (
                  <Box sx={{ p: 3 }}>
                    {/* Filters */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                      <TextField
                        placeholder="Search by customer, reference, or staff..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ minWidth: 250 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        }}
                      />
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Department</InputLabel>
                        <Select
                          value={departmentFilter}
                          onChange={(e) => setDepartmentFilter(e.target.value)}
                          label="Department"
                        >
                          <MenuItem value="all">All Departments</MenuItem>
                          <MenuItem value="Room Bookings">Room Bookings</MenuItem>
                          <MenuItem value="Food & Beverage">Food & Beverage</MenuItem>
                          <MenuItem value="Facilities">Facilities</MenuItem>
                          <MenuItem value="Other Incomes">Other Incomes</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="body2" color="text.secondary">
                        Total: {filteredTransactions.length} transactions | ₦{filteredTransactions.reduce((sum, t) => sum + parseFloat(t.amount), 0).toLocaleString()}
                      </Typography>
                    </Box>

                    {/* Transactions Table */}
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f8fafc' }}>
                            <TableCell>
                              <TableSortLabel
                                active={orderBy === 'date'}
                                direction={orderBy === 'date' ? order : 'asc'}
                                onClick={() => handleRequestSort('date')}
                              >
                                Date & Time
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Customer</TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'amount'}
                                direction={orderBy === 'amount' ? order : 'asc'}
                                onClick={() => handleRequestSort('amount')}
                              >
                                Amount
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>Payment Method</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Processed By</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paginatedTransactions.map((transaction, idx) => (
                            <TableRow key={idx} hover>
                              <TableCell>{new Date(transaction.date).toLocaleString()}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={transaction.department} 
                                  size="small"
                                  sx={{ bgcolor: alpha(transaction.department_color, 0.1), color: transaction.department_color, fontWeight: 500 }}
                                />
                              </TableCell>
                              <TableCell>{transaction.customer_name || 'N/A'}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>₦{transaction.amount?.toLocaleString()}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={transaction.payment_method} 
                                  size="small"
                                  sx={{ bgcolor: alpha(PAYMENT_METHOD_COLORS[transaction.payment_method] || '#6B7280', 0.1), color: PAYMENT_METHOD_COLORS[transaction.payment_method] || '#6B7280' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={transaction.status} 
                                  size="small"
                                  sx={{ bgcolor: alpha(STATUS_COLORS[transaction.status] || '#6B7280', 0.1), color: STATUS_COLORS[transaction.status] || '#6B7280' }}
                                />
                              </TableCell>
                              <TableCell>{transaction.processed_by || 'System'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50]}
                      component="div"
                      count={filteredTransactions.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={(e, newPage) => setPage(newPage)}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                    />
                  </Box>
                )}

                {/* Payment Analysis Tab */}
                {activeTab === 2 && (
                  <Box sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>Payment Methods by Department</Typography>
                    <Grid container spacing={3}>
                      {paymentAnalysis.map((dept, idx) => (
                        <Grid item xs={12} md={6} key={idx}>
                          <Paper 
                            sx={{ 
                              p: 2, 
                              borderRadius: 3,
                              borderLeft: `4px solid ${dept.color}`,
                              cursor: 'pointer',
                              transition: 'transform 0.2s',
                              '&:hover': { transform: 'translateX(4px)' }
                            }}
                            onClick={() => handlePaymentAnalysisClick(dept.department, dept)}
                          >
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                              {dept.department}
                            </Typography>
                            {dept.methods?.map((method, mIdx) => (
                              <Box key={mIdx} sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="body2">{method.payment_method}</Typography>
                                  <Typography variant="body2" fontWeight={600}>₦{method.total_amount?.toLocaleString()}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <Box sx={{ flexGrow: 1, bgcolor: '#e2e8f0', borderRadius: 2, overflow: 'hidden' }}>
                                    <Box 
                                      sx={{ 
                                        width: `${dept.total > 0 ? (method.total_amount / dept.total) * 100 : 0}%`, 
                                        bgcolor: PAYMENT_METHOD_COLORS[method.payment_method] || dept.color, 
                                        height: 6 
                                      }} 
                                    />
                                  </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {method.count} txns
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                            <Divider sx={{ my: 1.5 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" color="text.secondary">Total</Typography>
                              <Typography variant="subtitle2" fontWeight={700}>₦{dept.total?.toLocaleString()}</Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {/* Weekly Trend Tab */}
                {activeTab === 3 && (
                  <Box sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>Weekly Revenue Trend (Last 7 Days)</Typography>
                    <Box sx={{ height: 400 }}>
                      <Bar data={weeklyTrendData} options={barOptions} />
                    </Box>
                  </Box>
                )}
              </Paper>
            </>
          )}
        </Box>
      </Box>

      {/* Dialogs */}
      <TransactionDialog />
      <PaymentDialog />
      <ExpensesDialog />
    </LocalizationProvider>
  );
};

export default AccountDashboard;